import React from 'react';
import { Product } from '../../services/api/domains/products/types';
import { modalStyles as styles } from './styles';
import useEditableModal, { ModalMode } from './hooks/useEditableModal';
import ModalFieldRenderer from './ModalFieldRenderer';
import CollapsibleSection from './CollapsibleSection';
import OrangeBookPanel from './OrangeBookPanel';
import CASRegistryPanel from './CASRegistryPanel';
import {
  toSectionFields,
  getAlwaysDisabledFields,
} from '../../domains/products/adapters';
import { Manufacturer } from 'services/api/domains/manufacturers/types';
import { useABTest } from '../../contexts/ABTestContext';
import {
  FIELD_TYPE_SECTIONS,
  DATA_SOURCE_SECTIONS,
} from '../../domains/products/adapters/modalFieldAdapter';

interface ProductEditProps {
  product: Product;
  initialMode?: 'EDIT' | 'VIEW';
  save: (editedProduct: Product) => void;
  close: () => void;
  allManufacturers: Manufacturer[];
}

const ProductModal: React.FC<ProductEditProps> = ({
  product,
  initialMode = 'VIEW',
  save,
  close,
  allManufacturers,
}) => {
  const { getTestValue } = useABTest();
  const activeSections = getTestValue('modal_field_grouping')
    ? DATA_SOURCE_SECTIONS
    : FIELD_TYPE_SECTIONS;

  // Use the hook for all modal logic
  const {
    editedEntity: editedProduct,
    setEditedEntity: setEditedProduct,
    modalMode,
    focusedField,
    setFocusedField,
    handleSave,
    handleClose,
    isFieldDisabled,
    getFloatingLabelStyle,
    getFloatingInputStyle,
    getSaveButtonStyle,
    getSaveButtonHoverStyle,
    getSaveButtonFocusStyle,
    getCloseButtonStyle,
    getCloseButtonHoverStyle,
    getCloseButtonFocusStyle,
    getSaveButtonText,
    getCloseButtonText,
  } = useEditableModal({
    entity: product,
    initialMode,
    onSave: save,
    onClose: close,
    alwaysDisabledFields: getAlwaysDisabledFields(),
    styles,
  });

  return (
    <div style={styles.modalOverlayStyle}>
      <div style={styles.modalContentStyle}>
        <div style={styles.modalHeaderStyle}>
          <h2 style={styles.modalTitleStyle}>
            {modalMode === 'EDIT' ? 'Edit Product' : 'Product Details'}
          </h2>
          <button
            onClick={handleClose}
            style={styles.modalCloseXStyle}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, styles.modalCloseXHoverStyle)
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, styles.modalCloseXStyle)
            }>
            ×
          </button>
        </div>

        <div style={styles.modalBodyStyle}>
          {activeSections.map((section, idx) => {
            const sectionFields = toSectionFields(
              { manufacturers: allManufacturers },
              section
            );
            if (sectionFields.length === 0) return null;

            const fieldRenderer = (
              <ModalFieldRenderer
                key={section.title}
                fields={sectionFields}
                entity={editedProduct}
                setEntity={setEditedProduct}
                mode={modalMode}
                focusedField={focusedField}
                setFocusedField={setFocusedField}
                isFieldDisabled={isFieldDisabled}
                getFloatingLabelStyle={getFloatingLabelStyle}
                getFloatingInputStyle={getFloatingInputStyle}
                styles={styles}
                missingMessage="Unknown"
              />
            );

            const sectionEl = section.collapsible ? (
              <CollapsibleSection
                key={section.title}
                title={section.title}
                defaultCollapsed={section.defaultCollapsed}>
                {fieldRenderer}
              </CollapsibleSection>
            ) : (
              <div key={section.title}>
                <h3
                  style={{
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: `${idx === 0 ? 0 : 20}px 0 8px 0`,
                    paddingBottom: '6px',
                    borderBottom: '1px solid #e5e7eb',
                  }}>
                  {section.title}
                </h3>
                {fieldRenderer}
              </div>
            );

            // Inject the OB panel and CAS panel after the first section (name / Product Identity)
            if (idx === 0) {
              return (
                <React.Fragment key={`${section.title}-with-panels`}>
                  {sectionEl}
                  <OrangeBookPanel
                    obRecords={editedProduct.ob_records ?? []}
                    isFdaLinked={editedProduct.is_fda_linked ?? false}
                  />
                  <CASRegistryPanel
                    isCasVerified={editedProduct.is_cas_verified ?? false}
                    casNumber={editedProduct.cas_number}
                    synonyms={editedProduct.synonyms}
                    molecularFormula={editedProduct.molecular_formula}
                    molecularMass={editedProduct.molecular_mass}
                    inchi={editedProduct.inchi}
                    inchiKey={editedProduct.inchi_key}
                    smile={editedProduct.smile}
                    canonicalSmile={editedProduct.canonical_smile}
                    replacedRns={editedProduct.replaced_rns}
                    uri={editedProduct.uri}
                  />
                </React.Fragment>
              );
            }

            return sectionEl;
          })}
        </div>

        <div style={styles.modalFooterStyle}>
          <button
            onClick={handleClose}
            style={getCloseButtonStyle()}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, getCloseButtonHoverStyle())
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, getCloseButtonStyle())
            }
            onFocus={e =>
              Object.assign(e.currentTarget.style, getCloseButtonFocusStyle())
            }
            onBlur={e =>
              Object.assign(e.currentTarget.style, getCloseButtonStyle())
            }>
            {getCloseButtonText()}
          </button>
          <button
            onClick={handleSave}
            style={getSaveButtonStyle()}
            onMouseEnter={e =>
              Object.assign(e.currentTarget.style, getSaveButtonHoverStyle())
            }
            onMouseLeave={e =>
              Object.assign(e.currentTarget.style, getSaveButtonStyle())
            }
            onFocus={e =>
              Object.assign(e.currentTarget.style, getSaveButtonFocusStyle())
            }
            onBlur={e =>
              Object.assign(e.currentTarget.style, getSaveButtonStyle())
            }>
            {getSaveButtonText()}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProductModal);
export type { ModalMode };
